import React, { memo } from 'react';
import { defineMessages, useIntl, type MessageValue, type NoMessageValues } from 'react-intl';
import { StyleSheet, View, type ViewStyle } from 'react-native';

import { format, isValid, parseISO } from 'date-fns';

import { BodyText, TagLabel, theme } from '@sg/garnish';
import { CreditType } from '@sg/graphql-schema';

// ─────────────────────────────────────────────────────────────────────────────

export const CreditsListItem = memo((props: CreditsListItemProps) => {
  const {
    createdAtDate,
    expirationDate,
    title,
    isTitleBold,
    amount,
    creditType,
    hasTopBorder,
    borderTopColor = 'GRAY',
  } = props;

  const { formatMessage, formatNumber } = useIntl();

  // ─────────────────────────────────────────────────────────────────────

  const formattedAmount = formatNumber(Number(amount) / 100, {
    style: 'currency',
    currency: 'USD',
  });

  const createdAtDateFormatted = getExpirationDate(createdAtDate);
  const expirationDateObj = getExpirationDate(expirationDate);

  const expirationDateFormatted = expirationDateObj
    ? formatMessage(messages.expirationLabel, { date: expirationDateObj })
    : null;

  // ─────────────────────────────────────────────────────────────────────

  const label =
    creditType === CreditType.InStoreOnly ? formatMessage(messages.inStoreOnlyLabel) : null;

  // ─── Styles ──────────────────────────────────────────────────────────

  const containerBorderStyles = hasTopBorder
    ? [styles.containerWithBorder, borderStyles[borderTopColor]]
    : undefined;
  const containerStyles = [styles.container, containerBorderStyles];

  // ─────────────────────────────────────────────────────────────────────

  return (
    <View style={containerStyles}>
      <View style={styles.contentContainer}>
        {createdAtDateFormatted ? (
          <BodyText sizeMatch={['14']} bold>
            {createdAtDateFormatted}
          </BodyText>
        ) : null}

        <BodyText sizeMatch={['14']} bold={isTitleBold}>
          {title}
        </BodyText>

        {expirationDateFormatted ? (
          <BodyText sizeMatch={['12']} style={styles.expirationDate}>
            {expirationDateFormatted}
          </BodyText>
        ) : null}

        {label ? (
          <View style={styles.labelContainer}>
            <TagLabel>{label}</TagLabel>
          </View>
        ) : null}
      </View>

      <BodyText sizeMatch={['14']} bold>
        {formattedAmount}
      </BodyText>
    </View>
  );
});

// ─── Helpers ─────────────────────────────────────────────────────────────────

function getExpirationDate(expirationDate: CreditsListItemProps['expirationDate']): string | null {
  const isStringDate = typeof expirationDate === 'string';
  const expirationDateObj = isStringDate ? parseISO(expirationDate) : null;
  const hasValidExpirationDate = expirationDateObj !== null && isValid(expirationDateObj);

  if (!hasValidExpirationDate) return null;

  return format(expirationDateObj, 'MM/dd/yyyy');
}

// ─── Styles ──────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    paddingVertical: theme.spacing['6'],
  },
  containerWithBorder: {
    borderTopWidth: 1,
  },
  contentContainer: {
    flex: 1,
    alignItems: 'flex-start',
    marginRight: theme.spacing['2'],
  },
  expirationDate: {
    color: theme.colors.CHARCOAL,
    marginTop: theme.spacing['1'],
  },
  labelContainer: {
    marginTop: theme.spacing['2'],
  },
});

const borderStyles = StyleSheet.create<Record<CreditsListItemBorderColor, ViewStyle>>({
  GRAY: {
    borderTopColor: theme.colors.GRAY,
  },
  DARK_KALE: {
    borderTopColor: theme.colors.DARK_KALE,
  },
});

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages<{
  readonly expirationLabel: { readonly date: MessageValue };
  readonly inStoreOnlyLabel: NoMessageValues;
}>({
  expirationLabel: {
    defaultMessage: 'Expires {date}',
    description: 'Credits List Item | Expiration label',
  },
  inStoreOnlyLabel: {
    defaultMessage: 'In-store only',
    description: 'Credits List Item | In-store only label',
  },
});

// ─── Types ───────────────────────────────────────────────────────────────────

type CreditsListItemProps = Readonly<{
  title: string;
  isTitleBold?: boolean;
  amount: number;
  createdAtDate?: string | null;
  expirationDate?: string | null;
  creditType?: CreditType;
  hasTopBorder?: boolean;
  borderTopColor?: CreditsListItemBorderColor;
}>;

type CreditsListItemBorderColor = 'GRAY' | 'DARK_KALE';
